import React from 'react';
import LegalPage from './LegalPage';

export default function AccessibilityPage() {
  return (
    <LegalPage title="Accessibility" lastUpdated="April 30, 2026">
      <h2>1. Our Commitment</h2>
      <p>
        Tooling Management Group LLC (&ldquo;TMG&rdquo;) is committed to making the ToolSight&trade; platform accessible to all users, including individuals with disabilities. We strive to ensure that our Service meets or exceeds applicable accessibility standards so that every authorized user can effectively perform their role within the platform.
      </p>

      <h2>2. Standards We Follow</h2>
      <p>
        We design and develop the ToolSight platform with reference to the Web Content Accessibility Guidelines (WCAG) 2.1 at the AA conformance level. While we are working toward full conformance, our current efforts include:
      </p>
      <ul>
        <li>Semantic HTML structure to support screen readers and assistive technologies</li>
        <li>Keyboard navigability for core workflows</li>
        <li>Sufficient color contrast ratios for text and interactive elements</li>
        <li>Text labels and alternative text for meaningful images and icons</li>
        <li>Responsive design that functions across desktop and mobile devices</li>
      </ul>
      <p>
        As the ToolSight platform continues to evolve, accessibility improvements are incorporated into our development process alongside feature development.
      </p>

      <h2>3. Known Limitations</h2>
      <p>
        ToolSight is an actively developing platform. While we are committed to accessibility, certain areas may not yet fully conform to WCAG 2.1 AA standards. We are aware of and actively working to address:
      </p>
      <ul>
        <li>Complex data tables and grid views that may require enhanced screen reader support</li>
        <li>Certain interactive components that may have limited keyboard-only navigation</li>
        <li>Dynamically loaded content that may not always announce changes to assistive technologies</li>
      </ul>
      <p>
        We document known issues and prioritize accessibility improvements as part of our ongoing development roadmap.
      </p>

      <h2>4. Feedback and Assistance</h2>
      <p>
        We welcome feedback on the accessibility of the ToolSight platform. If you encounter an accessibility barrier or have suggestions for improvement, please contact us:
      </p>
      <p>
        <strong>Contact</strong><br />
        Tooling Management Group LLC<br />
        Attn: Chris Ongena, CEO<br />
        745 State Circle<br />
        Ann Arbor, MI 48108, USA<br />
        Email: <a href="mailto:chris@toolingmanagementgroup.com">chris@toolingmanagementgroup.com</a>
      </p>
      <p>
        <em>If you are an active user of the Service, your TMG account representative is also available for day-to-day questions.</em>
      </p>
      <p>
        When reporting an accessibility issue, please include a description of the issue, the page or feature where you encountered it, and the assistive technology you are using (if applicable). We will make reasonable efforts to respond promptly and to address reported barriers.
      </p>
      <p>
        If you need assistance using any feature of the Service due to an accessibility limitation, please contact your TMG engagement representative, who can provide alternative means of accessing the information or completing the task.
      </p>

      <h2>5. Continuous Improvement</h2>
      <p>
        TMG is committed to the ongoing improvement of accessibility across the ToolSight platform. Our approach includes:
      </p>
      <ul>
        <li>Incorporating accessibility requirements into the design and development of new features</li>
        <li>Reviewing existing features for accessibility as part of scheduled maintenance and updates</li>
        <li>Evaluating assistive technology compatibility as part of our testing process</li>
        <li>Training our development team on accessibility best practices</li>
      </ul>

      <h2>6. Last Updated</h2>
      <p>
        This Accessibility Statement was last reviewed and updated on April 30, 2026.
      </p>
    </LegalPage>
  );
}
